import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Modal} from 'react-bootstrap';
import {SPORTS} from '../constants';
import {getPartners, getPartnerFields} from '../api';
import {createAdminPartnerField} from '../api/field-copy';
import {isFieldCopy} from '../util/field-copy';

const errors = {
  source_nvr_upgrade_required: 'Il campo originale deve avere un NVR aggiornato e abilitato.',
  source_cameras_not_configured: 'Le camere del campo originale devono essere configurate sul NVR.',
  field_not_found: 'Il campo originale non è più disponibile.',
  invalid_field_copy: 'Seleziona un campo reale come originale.',
  partner_not_found: 'Il partner destinatario non è più disponibile.',
  invalid_request: 'Controlla i dati del campo.'
};

export default function AdminCreateField({partner, token, onHide, onCreated}) {
  const [type, setType] = useState('real');
  const [partners, setPartners] = useState([]);
  const [sourcePartner, setSourcePartner] = useState('');
  const [fields, setFields] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [values, setValues] = useState({name: '', sport: '', description: '', terrain: '', covered: false, bookable: true});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (type !== 'copy') return undefined;
    let active = true;
    setLoading(true);
    getPartners(token).then(result => {
      if (!active) return;
      if (result.status === 200) setPartners(result.json);
      else setError('Impossibile caricare i partner. Riprova.');
      setLoading(false);
    });
    return () => {active = false;};
  }, [type, token]);

  useEffect(() => {
    if (!sourcePartner) return undefined;
    let active = true;
    setLoading(true);
    setFields([]);
    setSourceId('');
    getPartnerFields(sourcePartner, token).then(result => {
      if (!active) return;
      if (result.status === 200) setFields(result.json.filter(field => !isFieldCopy(field)));
      else setError('Impossibile caricare i campi originali. Riprova.');
      setLoading(false);
    });
    return () => {active = false;};
  }, [sourcePartner, token]);

  const source = fields.find(field => String(field.id) === sourceId);
  const change = event => {
    const {name, value, type: inputType, checked} = event.target;
    setValues(current => ({...current, [name]: inputType === 'checkbox' ? checked : value}));
  };
  const submit = async event => {
    event.preventDefault();
    if (saving || (type === 'copy' && (!source || loading || !source.cameras.length))) return;
    setSaving(true);
    setError('');
    const result = await createAdminPartnerField(partner.uid, {...values, sourceFieldId: type === 'copy' ? source.id : null}, token);
    if (result.status === 201) {
      onCreated(result.json);
      onHide();
    } else {
      setError(errors[result.json.error] || 'Creazione non confermata. Verifica l’elenco dei campi prima di riprovare.');
      setSaving(false);
    }
  };

  return <Modal show onHide={saving ? () => {} : onHide} backdrop={saving ? 'static' : true} keyboard={!saving}>
    <Modal.Header closeButton={!saving}><Modal.Title>Crea campo per {partner.businessName}</Modal.Title></Modal.Header>
    <Form onSubmit={submit}>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="new-field-type">
          <Form.Label>Tipo di campo</Form.Label>
          <Form.Control as="select" value={type} disabled={saving} onChange={event => {setType(event.target.value); setError('');}}>
            <option value="real">Campo reale</option><option value="copy">Campo copia</option>
          </Form.Control>
        </Form.Group>
        {type === 'copy' && <>
          <Form.Group controlId="source-partner"><Form.Label>Partner del campo originale</Form.Label>
            <Form.Control required as="select" value={sourcePartner} disabled={saving} onChange={event => {setSourcePartner(event.target.value); setSourceId(''); setError('');}}>
              <option value="">Seleziona partner</option>
              {partners.map(item => <option key={item.uid} value={item.uid}>{item.businessName}</option>)}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="source-field"><Form.Label>Campo originale</Form.Label>
            <Form.Control required as="select" value={sourceId} disabled={loading || saving || !sourcePartner} onChange={event => {
              const id = event.target.value;
              setSourceId(id);
              const original = fields.find(field => String(field.id) === id);
              if (original) setValues(current => ({...current, sport: original.sport, terrain: original.terrain, covered: original.covered}));
            }}>
              <option value="">{loading ? 'Caricamento…' : 'Seleziona campo reale'}</option>
              {fields.map(field => <option key={field.id} value={field.id}>{field.name} (#{field.id})</option>)}
            </Form.Control>
          </Form.Group>
          {source && <Alert variant={source.cameras.length ? 'info' : 'warning'}>
            {source.cameras.length ? `${source.cameras.length} camere verranno aggiunte automaticamente: ${source.cameras.map(camera => camera.name).join(', ')}.` : 'Il campo originale non ha camere.'}
            <br/>Camere e XML tecnici seguiranno il campo originale. Prenotazioni, video e pubblicità rimarranno separati per questo partner.
          </Alert>}
        </>}
        <Form.Group controlId="new-field-name"><Form.Label>Nome del campo</Form.Label><Form.Control required name="name" maxLength={50} value={values.name} onChange={change} disabled={saving}/></Form.Group>
        <Form.Group controlId="new-field-sport"><Form.Label>Sport</Form.Label><Form.Control required as="select" name="sport" value={values.sport} disabled={saving} onChange={event => {change(event); setValues(current => ({...current, terrain: ''}));}}><option value="">Seleziona sport</option>{Object.keys(SPORTS).map(sport => <option key={sport}>{sport}</option>)}</Form.Control></Form.Group>
        <Form.Group controlId="new-field-terrain"><Form.Label>Terreno</Form.Label><Form.Control required as="select" name="terrain" value={values.terrain} onChange={change} disabled={saving}><option value="">Seleziona terreno</option>{(SPORTS[values.sport] ? SPORTS[values.sport].terrain : []).map(terrain => <option key={terrain}>{terrain}</option>)}</Form.Control></Form.Group>
        <Form.Group controlId="new-field-description"><Form.Label>Descrizione</Form.Label><Form.Control required name="description" maxLength={255} value={values.description} onChange={change} disabled={saving}/></Form.Group>
        <Form.Check id="new-field-covered" name="covered" label="Campo coperto" checked={values.covered} onChange={change} disabled={saving}/>
        <Form.Check id="new-field-bookable" name="bookable" label="Prenotabile" checked={values.bookable} onChange={change} disabled={saving}/>
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={saving} onClick={onHide}>Annulla</Button><Button type="submit" disabled={saving || (type === 'copy' && (loading || !source || !source.cameras.length))}>{saving ? 'Creazione…' : 'Crea campo'}</Button></Modal.Footer>
    </Form>
  </Modal>;
}
